import type { Definition } from '@exodus/dependency-types';
import type Emitter from '@exodus/wild-emitter';

import type { FetchWithRequestInterceptor } from '@/background/adapters/fetch';
import type { FiatProviderAtom } from '../atoms/provider.js';
import { FIAT_WIDGET_HEALTH_ENDPOINTS } from '../constants.js';
import type { WidgetAvailabilityState } from '../redux/types.js';

export const FIAT_WIDGET_AVAILABILITY_EVENT = 'fiatWidgetAvailability';

type Dependencies = {
  fiatProviderAtom: FiatProviderAtom;
  port: Emitter;
  fetch: FetchWithRequestInterceptor;
  config: {
    serverUrl: string;
  };
};

const createFiatRampWidgetPlugin = ({
  fiatProviderAtom,
  port,
  fetch,
  config,
}: Dependencies) => {
  const signUrls = new Set([
    new URL('/api/v1/sign', config.serverUrl).toString(),
    new URL('/api/sign', config.serverUrl).toString(), // Legacy, can be removed after upgrading to latest fiat-ramp.
  ]);

  const fixAppIdHeader = (url: string, init: RequestInit): RequestInit => {
    if (!signUrls.has(url)) {
      return init;
    }

    const headers = new Headers(init.headers ?? {});
    // This controls the key used to sign the wallet address. We need this to be overridden when the wallet is embedded,
    // otherwise the wrong signing key is used (as embedded wallet ids are of the form passkeys/<id>).
    headers.set('x-exodus-app-id', 'passkeys');

    return {
      ...init,
      headers,
    };
  };

  fetch.setRequestInterceptor(fixAppIdHeader);

  const emit = (available: boolean, loaded: boolean = true) => {
    port.emit(FIAT_WIDGET_AVAILABILITY_EVENT, {
      loaded,
      available,
    } satisfies WidgetAvailabilityState);
  };

  const fetchWidgetHealth = async (url: string) => {
    const response = await fetch(url, { method: 'head' });
    return response?.status === 200;
  };

  return {
    onLoad: async () => {
      try {
        const providerName = await fiatProviderAtom.get();

        const url = FIAT_WIDGET_HEALTH_ENDPOINTS[providerName];
        if (!url) {
          emit(true);
          return;
        }

        const available = await fetchWidgetHealth(url);
        if (available) {
          emit(true);
          return;
        }
      } catch {}

      emit(false);
    },
    onStop: () => {
      emit(false, false);
    },
  };
};

const fiatRampWidgetPluginDefinition = {
  id: 'fiatRampWidgetPlugin',
  type: 'plugin',
  factory: createFiatRampWidgetPlugin,
  dependencies: ['fiatProviderAtom', 'port', 'fetch', 'config'],
} as const satisfies Definition;

export default fiatRampWidgetPluginDefinition;
